import AppLayout from '@/components/AppLayout';
import { useLanguage } from '@/contexts/LanguageContext';

export default function PrivacyPolicy() {
  const { t } = useLanguage();
  
  return (
    <AppLayout>
      <div className="max-w-3xl mx-auto py-8 px-4">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">PRIVACY POLICY – SERVICEGO</h1>
        <p className="text-sm text-gray-500 mb-8">Ultimo aggiornamento: Dicembre 2025</p>
        
        <div className="prose prose-sm max-w-none space-y-6 text-gray-700">
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Titolare del trattamento</h2>
            <p>
              Il titolare del trattamento dei dati personali relativi alla piattaforma ServiceGo è 
              <strong> I.F. Core Lab</strong>, di seguito denominata "Titolare".
            </p>
            <p className="mt-3">
              Per qualsiasi richiesta relativa alla presente Privacy Policy è possibile contattare il Titolare 
              all'indirizzo email <a href="mailto:support@servicego.ch" className="text-blue-600 hover:underline">support@servicego.ch</a>.
            </p>
          </section>
          
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Ambito di applicazione</h2>
            <p>
              La presente Privacy Policy disciplina il trattamento dei dati personali degli utenti che accedono, 
              si registrano o utilizzano la piattaforma digitale ServiceGo, sia tramite applicazione mobile 
              sia tramite sito web.
            </p>
            <p className="mt-3">
              Il trattamento dei dati avviene in conformità alla Legge federale svizzera sulla protezione dei dati 
              (LPD, revisione 2023) e, ove applicabile, al Regolamento UE 2016/679 (GDPR).
            </p>
          </section>
          
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Tipologie di dati trattati</h2>
            <p>Il Titolare può raccogliere e trattare le seguenti categorie di dati personali:</p>
            <ul className="list-disc pl-5 space-y-3 mt-3">
              <li>
                <strong>Dati forniti direttamente dall'utente:</strong> nome, cognome, email, numero di telefono, 
                dati aziendali, informazioni di contatto, contenuti inseriti nei profili, annunci, messaggi, 
                richieste o offerte.
              </li>
              <li>
                <strong>Dati tecnici e di utilizzo:</strong> indirizzo IP, dati di accesso, log di sistema, 
                identificativi del dispositivo, browser, sistema operativo, data e ora di accesso, 
                attività svolte sulla piattaforma.
              </li>
              <li>
                <strong>Contenuti caricati dall'utente:</strong> testi, comunicazioni, preventivi, documenti, 
                immagini o altri materiali caricati volontariamente.
              </li>
            </ul>
            <p className="mt-3 font-semibold">
              L'utente è l'unico ed esclusivo responsabile dei dati e dei contenuti che decide di caricare 
              o condividere tramite la piattaforma.
            </p>
          </section>
          
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Finalità del trattamento</h2>
            <p>I dati personali sono trattati esclusivamente per le seguenti finalità:</p>
            <ul className="list-disc pl-5 space-y-2 mt-3">
              <li>Consentire la registrazione e l'utilizzo della piattaforma</li>
              <li>Permettere il funzionamento tecnico e operativo del servizio</li>
              <li>Facilitare il contatto tra utenti</li>
              <li>Gestire pagamenti e fatturazione</li>
              <li>Adempiere a obblighi legali e fiscali</li>
              <li>Prevenire abusi o utilizzi illeciti</li>
              <li>Tutelare i diritti e gli interessi del Titolare in sede giudiziaria o stragiudiziale</li>
            </ul>
          </section>
          
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Base giuridica del trattamento</h2>
            <p>Il trattamento dei dati si fonda su:</p>
            <ul className="list-disc pl-5 space-y-2 mt-3">
              <li>L'esecuzione del rapporto contrattuale derivante dall'accettazione dei Termini e Condizioni</li>
              <li>L'adempimento di obblighi legali</li>
              <li>Il legittimo interesse del Titolare alla gestione e sicurezza della piattaforma</li>
              <li>Ove richiesto, il consenso espresso dell'utente</li>
            </ul>
          </section>
          
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Ruolo della piattaforma e limitazione di responsabilità</h2>
            <p>
              ServiceGo è una <strong>piattaforma tecnica</strong>.
            </p>
            <p className="mt-3">
              Il Titolare non verifica, non controlla e non garantisce la veridicità, completezza o liceità 
              dei dati inseriti dagli utenti.
            </p>
            <p className="mt-3">
              Il Titolare non assume alcuna responsabilità per contenuti illeciti, inesatti, incompleti o 
              non aggiornati pubblicati dagli utenti.
            </p>
            <p className="mt-3 font-semibold">
              L'utente manleva espressamente il Titolare da qualsiasi responsabilità, danno, richiesta o pretesa 
              derivante dai dati o contenuti caricati, comunicati o diffusi tramite la piattaforma.
            </p>
          </section>
          
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Conservazione dei dati</h2>
            <p>
              I dati personali sono conservati per il tempo strettamente necessario alle finalità per cui sono stati 
              raccolti e per adempiere a obblighi legali o difendere i diritti del Titolare.
            </p>
            <p className="mt-3">
              Il Titolare si riserva il diritto di conservare determinati dati anche successivamente alla cancellazione 
              dell'account, nei limiti consentiti dalla legge, in particolare per finalità legali, fiscali o di sicurezza.
            </p>
          </section>
          
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Comunicazione e trasferimento dei dati</h2>
            <p>I dati personali possono essere comunicati a:</p>
            <ul className="list-disc pl-5 space-y-2 mt-3">
              <li>Fornitori tecnici e informatici</li>
              <li>Servizi di hosting e cloud</li>
              <li>Fornitori di servizi di pagamento</li>
              <li>Consulenti legali e fiscali</li>
              <li>Autorità competenti ove richiesto dalla legge</li>
            </ul>
            <p className="mt-3">
              I dati non vengono venduti né ceduti a terzi per finalità commerciali.
            </p>
            <p className="mt-3">
              Eventuali trasferimenti di dati all'estero avvengono esclusivamente verso Paesi che garantiscono 
              un livello di protezione adeguato o tramite l'adozione di idonee garanzie legali.
            </p>
          </section>
          
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Sicurezza dei dati</h2>
            <p>
              Il Titolare adotta misure tecniche e organizzative adeguate per proteggere i dati personali da accessi 
              non autorizzati, perdita, distruzione o uso illecito.
            </p>
            <p className="mt-3">
              Tuttavia, l'utente prende atto che nessun sistema informatico può garantire sicurezza assoluta 
              e accetta il rischio residuo connesso all'utilizzo della piattaforma.
            </p>
          </section>
          
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Diritti degli utenti</h2>
            <p>
              Nei limiti previsti dalla legge, l'utente ha il diritto di:
            </p>
            <ul className="list-disc pl-5 space-y-2 mt-3">
              <li>Accedere ai propri dati personali</li>
              <li>Richiederne la rettifica</li>
              <li>Richiederne la cancellazione</li>
              <li>Richiederne la limitazione del trattamento</li>
              <li>Opporsi al trattamento</li>
              <li>Richiedere la portabilità dei dati</li>
            </ul>
            <p className="mt-3">
              Le richieste devono essere inviate al Titolare all'indirizzo 
              <a href="mailto:support@servicego.ch" className="text-blue-600 hover:underline ml-1">support@servicego.ch</a>.
            </p>
            <p className="mt-3">
              Il Titolare si riserva il diritto di rifiutare richieste manifestamente infondate, eccessive 
              o contrarie agli obblighi legali di conservazione.
            </p>
          </section>
          
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Cookie e strumenti di tracciamento</h2>
            <p>
              La piattaforma utilizza cookie tecnici e strumenti strettamente necessari al funzionamento del servizio.
            </p>
            <p className="mt-3">
              L'utilizzo continuato della piattaforma implica accettazione dell'uso di tali strumenti.
            </p>
          </section>
          
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Modifiche alla Privacy Policy</h2>
            <p>
              Il Titolare si riserva il diritto di modificare in qualsiasi momento la presente Privacy Policy.
            </p>
            <p className="mt-3">
              L'uso continuato della piattaforma successivamente alle modifiche costituisce accettazione 
              automatica delle stesse.
            </p>
          </section>
          
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Legge applicabile e foro competente</h2>
            <p>
              La presente Privacy Policy è regolata esclusivamente dal diritto svizzero.
            </p>
            <p className="mt-3 font-semibold">
              Per qualsiasi controversia è competente in via esclusiva il Foro di Lugano, Svizzera.
            </p>
          </section>
          
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Contatti</h2>
            <p>
              <strong>I.F. Core Lab</strong><br />
              Email: <a href="mailto:support@servicego.ch" className="text-blue-600 hover:underline">support@servicego.ch</a>
            </p>
          </section>
        </div>
        
        <div className="mt-12 pt-6 border-t border-gray-200 text-center text-xs text-gray-500">
          © 2025 I.F. Core Lab - Tutti i diritti riservati
        </div>
      </div>
    </AppLayout>
  );
}
